import {DotLottieReact} from '@lottiefiles/dotlottie-react'
import {Breadcrumb, Breadcrumbs} from 'react-aria-components'

import TrashSvg from '@/assets/icons/trash.svg?react'
import spinnerLottie from '@/assets/lotties/spinner.lottie'
import ProductPrice from '@/components/ProductCard/ProductPrice'
import ProductRetailerImage from '@/components/ProductCard/ProductRetailerImage'
import QuantitySelector from '@/components/QuantitySelector/QuantitySelector'
import SkipLink from '@/components/SkipLink'
import Footer from '@/layout/Footer'
import Navbar from '@/layout/Navbar'
import useAddToOrRemoveFromCartMutation from '@/lib/cart/hooks/useAddToOrRemoveFromCartMutation'
import {useCart} from '@/lib/cart/hooks/useCart'
import HeadTags from '@/lib/head/HeadTags'
import {useLocale} from '@/lib/locale/locale'
import PaymentProvider from '@/lib/payment/PaymentProvider'
import {type CartItem} from '@/models/cartModel'
import {CartRoute, HomeRoute, ProductRoute} from '@/routeRegistry'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {useIsMobile} from '@/utils/hooks/useWindowSize'
import RACLink from '@/utils/router/RACLink'

import style from './Cart.module.scss'
import CartSummary from './CartSummary'

const CartItem = memo(function CartItem({item, isMobile}: {item: CartItem; isMobile: boolean}) {
  const latestItem = useLatest(item)

  const {mutate: addToOrRemoveFromCart} = useAddToOrRemoveFromCartMutation()

  const handleQuantityChange = useCallback(
    (q: number) => {
      addToOrRemoveFromCart({
        items: [
          {
            ...latestItem.current,
            quantity: q,
          },
        ],
        replace: true,
      })
    },
    [addToOrRemoveFromCart],
  )

  const handleRemoveItem = useCallback(() => {
    addToOrRemoveFromCart({
      items: [
        {
          ...latestItem.current,
          quantity: 0,
        },
      ],
      replace: true,
    })
  }, [addToOrRemoveFromCart])

  const options = item.variation ? Array.from(Object.entries(item.variation)) : undefined

  if (isMobile) {
    return (
      <>
        <tr key={item.id}>
          {/* eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role -- this is okay */}
          <td role='presentation'>
            <div className={style.Item}>
              <div aria-hidden>
                <Link
                  to={ProductRoute.fullPath}
                  params={{
                    productId: {
                      retailer: item.retailer,
                      id: item.id,
                    },
                  }}
                >
                  <img src={item.image} alt={item.title} />
                </Link>
              </div>
              <div className={style.ItemInfo}>
                <div role='cell'>
                  <VisuallyHidden as='div'>
                    <Link
                      to={ProductRoute.fullPath}
                      params={{
                        productId: {
                          retailer: item.retailer,
                          id: item.id,
                        },
                      }}
                    >
                      <img src={item.image} alt={item.title} />
                    </Link>
                  </VisuallyHidden>
                  <div className={style.ItemName}>
                    <Link
                      to={ProductRoute.fullPath}
                      params={{
                        productId: {
                          retailer: item.retailer,
                          id: item.id,
                        },
                      }}
                    >
                      {item.title}
                    </Link>
                  </div>
                  <div className={style.VariantOption}>
                    {options ? (
                      options.map(([name, value]) => (
                        <div key={name}>
                          <span>{name}:</span> <span>{value}</span>
                        </div>
                      ))
                    ) : (
                      <div>No optiton</div>
                    )}
                  </div>
                </div>
                <VisuallyHidden role='cell'>{item.seller ?? item.retailer}</VisuallyHidden>
                <div aria-hidden>
                  <ProductPrice price={item.price * item.quantity} />
                </div>
                <div role='cell'>
                  <QuantitySelector
                    allowZero
                    quantity={item.quantity}
                    setQuantity={handleQuantityChange}
                  />
                </div>
                <VisuallyHidden role='cell'>
                  <ProductPrice price={item.price * item.quantity} />
                </VisuallyHidden>
                <div role='cell' className={style.ItemRemove}>
                  <button
                    className={style.RemoveItemBtn}
                    onClick={handleRemoveItem}
                    aria-label={`Remove ${item.title} from cart`}
                  >
                    <TrashSvg className={style.TrashIcon} />
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
        {/* <VisuallyHidden as='tr' strict>
          <td>
            <TRLink
              to='/product/$productId'
              params={{
                productId: {
                  retailer: item.retailer,
                  id: item.id,
                },
              }}
            >
              <img src={item.image} alt={item.title} />
            </TRLink>
            <TRLink
              to='/product/$productId'
              params={{
                productId: {
                  retailer: item.retailer,
                  id: item.id,
                },
              }}
            >
              {item.title}
            </TRLink>
          </td>
          <td>{item.seller ?? item.retailer}</td>
          <td>
            <QuantitySelector
              allowZero
              quantity={item.quantity}
              setQuantity={handleQuantityChange}
            />
          </td>
          <td>
            <ProductPrice price={item.price * item.quantity} />
          </td>
          <td>
            <button
              className={style.RemoveItemBtn}
              onClick={handleRemoveItem}
              aria-label={`Remove ${item.title} from cart`}
            >
              <TrashSvg className={style.TrashIcon} />
            </button>
          </td>
        </VisuallyHidden> */}
      </>
    )
  }

  return (
    <tr key={item.id}>
      <td>
        <div className={style.Item}>
          <div>
            <Link
              to='/product/$productId'
              params={{
                productId: {
                  retailer: item.retailer,
                  id: item.id,
                },
              }}
            >
              <img src={item.image} alt={item.title} />
            </Link>
          </div>
          <div className={style.ItemInfo}>
            <div className={style.ItemName}>
              <Link
                to='/product/$productId'
                params={{
                  productId: {
                    retailer: item.retailer,
                    id: item.id,
                  },
                }}
              >
                {item.title}
              </Link>
            </div>
            <div className={style.VariantOption}>
              {options ? (
                options.map(([name, value]) => (
                  <div key={name}>
                    <span>{name}:</span> <span>{value}</span>
                  </div>
                ))
              ) : (
                <div>No option</div>
              )}
            </div>
          </div>
        </div>
      </td>
      <VisuallyHidden as='td'>{item.seller ?? item.retailer}</VisuallyHidden>
      <td>
        <QuantitySelector allowZero quantity={item.quantity} setQuantity={handleQuantityChange} />
      </td>
      <td>
        <ProductPrice price={item.price * item.quantity} />
      </td>
      <td>
        <button
          className={style.RemoveItemBtn}
          onClick={handleRemoveItem}
          aria-label={`Remove ${item.title} from cart`}
        >
          <TrashSvg className={style.TrashIcon} />
        </button>
      </td>
    </tr>
  )
})

export default memo(function Cart() {
  const isMobile = useIsMobile()
  const [locale] = useLocale()
  const {coupon, applyRewards} = CartRoute.useSearch()
  const {data: cart} = useCart(locale)

  const cartItemNumber = cart?.itemsLength ?? 0

  const isEmpty = !cart || cart.itemsLength === 0

  return (
    <PaymentProvider>
      <HeadTags title='Cart' />
      <SkipLink title='Skip to main content' to='#main-content' />
      <div className={style.Page}>
        <Navbar
          transparentBg
          withoutLogoOnDesktop
          variant='light'
          className={style.Navbar}
          haveCart={false}
        />
        <main className={style.CartPage} {...skipTargetProps('main-content')}>
          <div className={style.CartContent}>
            <div className={style.BreadcrumbsContainer} role='region' aria-label='breadcrumb'>
              <Breadcrumbs className={style.Breadcrumbs}>
                <Breadcrumb className={style.Breadcrumb}>
                  <RACLink className={style.Link} to={HomeRoute.fullPath}>
                    Home
                  </RACLink>
                </Breadcrumb>
                <Breadcrumb className={style.Breadcrumb}>
                  <RACLink className={style.Link} to={CartRoute.fullPath} search={{
                    applyRewards,
                    coupon,
                  }}>
                    Cart
                  </RACLink>
                </Breadcrumb>
              </Breadcrumbs>
            </div>
            <h1 className={style.Heading}>Your cart</h1>
            <div className={style.Cart}>
              <div className={style.CartItems}>
                <table className={style.CartTable}>
                  <caption>{`All products (${cartItemNumber} products)`}</caption>
                  <VisuallyHidden as='thead' isVisible={!isMobile}>
                    <tr>
                      <th style={{width: '100%'}}>
                        <VisuallyHidden>Product</VisuallyHidden>
                      </th>
                      <VisuallyHidden as='th'>Retailer</VisuallyHidden>
                      <th>Quantity</th>
                      <th>Price</th>
                      <VisuallyHidden as='th'>Actions</VisuallyHidden>
                    </tr>
                  </VisuallyHidden>
                  <tbody>
                    {!cart && (
                      <tr>
                        <td colSpan={5}>
                          <div className={style.SpinnerContainer}>
                            <DotLottieReact
                              src={spinnerLottie}
                              autoplay
                              loop
                              className={style.Spinner}
                              aria-label='Loading product...'
                              role='progressbar'
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                    {isEmpty && (
                      <tr>
                        <td colSpan={5}>
                          <div className={style.EmptyCart}>Your cart is empty</div>
                        </td>
                      </tr>
                    )}
                    {/* TODO: remove item animation */}
                    {cart &&
                      Object.entries(cart.items).map(([retailer, items]) => {
                        return (
                          <Fragment key={retailer}>
                            <tr aria-hidden>
                              <td colSpan={4}>
                                <div className={style.RetailerContainer}>
                                  <ProductRetailerImage retailer={retailer} shouldDisplayText />
                                  {/* TODO: hide & show products */}
                                  {/* <button tabIndex={-1}>
                                    <ChevronDownSvg aria-hidden />
                                  </button> */}
                                </div>
                              </td>
                            </tr>
                            {items.map(item => (
                              <CartItem key={item.id} item={item} isMobile={isMobile} />
                            ))}
                          </Fragment>
                        )
                      })}
                  </tbody>
                </table>
              </div>
              {!isMobile && <CartSummary />}
            </div>
          </div>
        </main>
        {isMobile && <CartSummary />}
        <Footer />
      </div>
    </PaymentProvider>
  )
})
